import { useEffect, useRef, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { resolveClientSession, syncAccessToken } from '@/lib/auth-session';
import { clearAllSupabaseAuthStorage } from '@/lib/auth-persistence';
import { api } from '@/services/api.service';
import { useAuthStore } from './auth.store';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { setSession, setUser, setLoading, clear } = useAuthStore();
  const hydratedRef = useRef(false);
  const profileLoadRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = () => {
      if (profileLoadRef.current) return profileLoadRef.current;
      profileLoadRef.current = api
        .getMe()
        .then((profile) => {
          if (isMounted) setUser(profile);
        })
        .catch((err) => {
          console.warn('[portal AuthProvider] profile load failed', err);
        })
        .finally(() => {
          profileLoadRef.current = null;
        });
      return profileLoadRef.current;
    };

    const applySession = (session: Session | null) => {
      if (!isMounted) return;
      setSession(session);
      hydratedRef.current = true;
      setLoading(false);
      syncAccessToken(session);
      if (session) void loadProfile();
      else setUser(null);
    };

    void resolveClientSession().then((session) => applySession(session));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!hydratedRef.current && event === 'INITIAL_SESSION') return;
      setSession(session);
      setLoading(false);
      syncAccessToken(session);
      if (session) {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
          void loadProfile();
        }
      } else {
        clearAllSupabaseAuthStorage();
        clear();
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [clear, setLoading, setSession, setUser]);

  return children;
}
